// The ramp onto a footbridge: a low stone abutment with a plank lip and four
// railing knobs. Authored symmetric so it reads in any span direction. Ported
// 1:1 from age-of-mcp's footbridge_head.ts: local abutment()/cornerPost()
// generators plus the shared foundationSlab/scaffold helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, type Part } from "../../lib";

const STONE = "#8d8a82";
const STONE_DARK = "#6f6c64";
const PLANK = "#9a6a38";
const RAIL = "#7a5a32";

function abutment(h: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.7, h, 0.7), color: STONE, position: [0, h / 2, 0] },
    { geometry: new THREE.BoxGeometry(0.76, 0.06, 0.76), color: STONE_DARK, position: [0, h, 0] },
  ];
}

function cornerPost(x: number, z: number, topY: number): Part {
  return { geometry: new THREE.BoxGeometry(0.08, 0.26, 0.08), color: RAIL, position: [x, topY + 0.13, z] };
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.78, 0.08)];
  if (s === 1) return [...abutment(0.16), ...scaffold(0.7, 0.2)];
  if (s === 2) return [...abutment(0.3), ...scaffold(0.7, 0.34)];
  return [
    ...abutment(0.34),
    { geometry: new THREE.BoxGeometry(0.62, 0.04, 0.62), color: STONE_DARK, position: [0, 0.34 + 0.04, 0] },
    { geometry: new THREE.BoxGeometry(0.56, 0.05, 0.16), color: PLANK, position: [0, 0.34 + 0.07, -0.2] },
    { geometry: new THREE.BoxGeometry(0.56, 0.05, 0.16), color: PLANK, position: [0, 0.34 + 0.07, 0.2] },
    cornerPost(-0.3, -0.3, 0.34), cornerPost(0.3, -0.3, 0.34),
    cornerPost(-0.3, 0.3, 0.34), cornerPost(0.3, 0.3, 0.34),
  ];
}

export const footbridgeHead = defineModel({
  id: "footbridge-head",
  tiles: { w: 1, h: 1 },
  batchId: "standard",
  name: "Footbridge head",
  tags: ["building","footbridge","head","low-poly","age-of-mcp","classic","water"],
  summary: "Low-poly footbridge head.",
  description: "A ramp onto a footbridge; a pair on opposite banks spans a gap with a plank walkway.",
  author: "age-of-mcp",
  footprint: 0.42,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
